'use client'

import { useEffect, useRef } from 'react'
import { ProctoringEventType } from '@/constants/enums'
import {
    createFocusMonitorState,
    getFocusEventsFromBlur,
    getFocusEventsFromFocus,
    getFocusEventsFromPageHide,
    getFocusEventsFromVisibilityChange,
    type FocusEventKind,
} from './focus-monitor.util'

const FOCUS_EVENT_TYPE_MAP: Record<FocusEventKind, ProctoringEventType> = {
    TabHidden: ProctoringEventType.TabHidden,
    TabVisible: ProctoringEventType.TabVisible,
    WindowBlur: ProctoringEventType.WindowBlur,
    WindowFocus: ProctoringEventType.WindowFocus,
}

export interface UseProctoringFocusMonitorOptions {
    enabled: boolean
    addProctoringEvent: (eventType: ProctoringEventType) => void
    flushProctoringEvents?: () => void
}

export function useProctoringFocusMonitor({ enabled, addProctoringEvent, flushProctoringEvents }: UseProctoringFocusMonitorOptions) {
    const stateRef = useRef(createFocusMonitorState())
    const addProctoringEventRef = useRef(addProctoringEvent)
    const flushProctoringEventsRef = useRef(flushProctoringEvents)

    addProctoringEventRef.current = addProctoringEvent
    flushProctoringEventsRef.current = flushProctoringEvents

    useEffect(() => {
        if (!enabled) return

        stateRef.current = createFocusMonitorState()

        const emit = (events: FocusEventKind[], shouldFlush = false) => {
            for (const event of events) {
                addProctoringEventRef.current(FOCUS_EVENT_TYPE_MAP[event])
            }
            if (shouldFlush && events.length > 0) {
                flushProctoringEventsRef.current?.()
            }
        }

        const onVisibilityChange = () => {
            const visibilityState = document.visibilityState === 'hidden' ? 'hidden' : 'visible'
            const { events, nextState } = getFocusEventsFromVisibilityChange(visibilityState, stateRef.current)
            stateRef.current = nextState
            emit(events, visibilityState === 'hidden')
        }

        const onBlur = () => {
            const visibilityState = document.visibilityState === 'hidden' ? 'hidden' : 'visible'
            const { events, nextState } = getFocusEventsFromBlur(visibilityState, stateRef.current)
            stateRef.current = nextState
            emit(events)
        }

        const onFocus = () => {
            const visibilityState = document.visibilityState === 'hidden' ? 'hidden' : 'visible'
            const { events, nextState } = getFocusEventsFromFocus(visibilityState, stateRef.current)
            stateRef.current = nextState
            emit(events)
        }

        const onPageHide = () => {
            const { events, nextState } = getFocusEventsFromPageHide(stateRef.current)
            stateRef.current = nextState
            emit(events, true)
        }

        window.addEventListener('blur', onBlur)
        window.addEventListener('focus', onFocus)
        window.addEventListener('pagehide', onPageHide)
        document.addEventListener('visibilitychange', onVisibilityChange)

        return () => {
            window.removeEventListener('blur', onBlur)
            window.removeEventListener('focus', onFocus)
            window.removeEventListener('pagehide', onPageHide)
            document.removeEventListener('visibilitychange', onVisibilityChange)
        }
    }, [enabled])
}
